import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Search, CornerDownLeft, ArrowUp, ArrowDown } from 'lucide-react';
import { Portal } from '../../utils/Portal';
import { cn } from '../../utils/cn';

/**
 * CommandPalette — recherche universelle ⌘K.
 *
 *   <CommandPalette
 *     isOpen={open}
 *     onClose={() => setOpen(false)}
 *     query={query}
 *     onQueryChange={setQuery}
 *     groups={[
 *       { label: 'Articles', items: [
 *         { id, label, hint?, icon?, onSelect: () => navigate(...) }
 *       ]},
 *       { label: 'Actions', items: [...] }
 *     ]}
 *     isLoading={false}
 *   />
 *
 * Le composant gère navigation clavier (↑/↓/Enter/Esc) et focus auto sur input.
 */
export function CommandPalette({
  isOpen,
  onClose,
  query = '',
  onQueryChange,
  groups = [],
  isLoading = false,
  placeholder = 'Rechercher…',
  emptyMessage = 'Aucun résultat',
  loadingMessage = 'Recherche…',
  className,
}) {
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);

  // Aplatit tous les items pour la navigation clavier
  const flatItems = useMemo(
    () => groups.flatMap((g) => g.items.map((it) => ({ ...it, _group: g.label }))),
    [groups],
  );

  // Reset l'index actif quand la liste change (nouvelle recherche)
  useEffect(() => {
    setActiveIndex(0);
  }, [query, groups.length]);

  // Auto-focus à l'ouverture
  useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [isOpen]);

  // Scroll vers l'item actif
  useEffect(() => {
    if (!listRef.current) return;
    const active = listRef.current.querySelector('[data-cmd-active="true"]');
    if (active) active.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  // Lock body scroll
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [isOpen]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose?.();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, flatItems.length - 1));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      const it = flatItems[activeIndex];
      if (it) {
        it.onSelect?.();
        onClose?.();
      }
    }
  }, [flatItems, activeIndex, onClose]);

  if (!isOpen) return null;

  let runningIndex = -1;

  return (
    <Portal>
      <div className="fixed inset-0 z-modal flex items-start justify-center pt-[10vh] px-4">
        <div
          aria-hidden
          onClick={onClose}
          className="absolute inset-0 bg-ink/40 backdrop-blur-[2px] animate-fade-in"
        />
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Recherche"
          className={cn(
            'relative w-full max-w-xl bg-surface border border-border rounded-xl shadow-xl animate-scale-in overflow-hidden',
            className,
          )}
          onKeyDown={handleKeyDown}
        >
          {/* Champ de recherche */}
          <div className="flex items-center gap-3 px-4 h-14 border-b border-border">
            <Search className="w-5 h-5 text-mute shrink-0" strokeWidth={1.8} />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => onQueryChange?.(e.target.value)}
              placeholder={placeholder}
              className="flex-1 bg-transparent text-[15px] outline-none placeholder:text-mute-2"
            />
            <kbd className="hidden sm:inline-flex items-center justify-center px-1.5 h-5 text-[10.5px] font-mono text-mute border border-border rounded">esc</kbd>
          </div>

          {/* Résultats */}
          <div ref={listRef} className="max-h-[50vh] overflow-y-auto py-1">
            {isLoading && (
              <div className="px-4 py-8 text-center text-sm text-mute">{loadingMessage}</div>
            )}
            {!isLoading && groups.every((g) => g.items.length === 0) && (
              <div className="px-4 py-8 text-center text-sm text-mute italic">{emptyMessage}</div>
            )}
            {!isLoading && groups.map((group) => (
              group.items.length === 0 ? null : (
                <div key={group.label}>
                  <div className="px-3 pt-3 pb-1 text-[10.5px] font-mono uppercase tracking-[0.18em] text-mute">
                    {group.label}
                  </div>
                  {group.items.map((item) => {
                    runningIndex += 1;
                    const isActive = runningIndex === activeIndex;
                    const idxForHandler = runningIndex;
                    return (
                      <button
                        key={item.id}
                        type="button"
                        data-cmd-active={isActive}
                        onClick={() => { item.onSelect?.(); onClose?.(); }}
                        onMouseEnter={() => setActiveIndex(idxForHandler)}
                        className={cn(
                          'w-full text-left px-3 py-2 flex items-center gap-3 transition-colors',
                          isActive ? 'bg-bg-2' : 'hover:bg-bg-2/60',
                        )}
                      >
                        {item.icon && (
                          <span className={cn('w-6 h-6 rounded-md flex items-center justify-center shrink-0',
                            isActive ? 'bg-ink text-cream' : 'bg-bg-2 text-mute')}>
                            {item.icon}
                          </span>
                        )}
                        <span className="flex-1 min-w-0">
                          <span className="block text-sm text-text leading-tight truncate">{item.label}</span>
                          {item.hint && (
                            <span className="block text-[11.5px] text-mute mt-0.5 truncate">{item.hint}</span>
                          )}
                        </span>
                        {item.shortcut && (
                          <kbd className="font-mono text-[10.5px] text-mute border border-border rounded px-1.5 py-0.5">{item.shortcut}</kbd>
                        )}
                        {isActive && (
                          <CornerDownLeft className="w-3.5 h-3.5 text-mute shrink-0" strokeWidth={1.8} />
                        )}
                      </button>
                    );
                  })}
                </div>
              )
            ))}
          </div>

          {/* Pied — astuces clavier */}
          <div className="px-3 py-2 border-t border-border bg-bg-2/40 flex items-center gap-4 text-[11px] text-mute">
            <span className="inline-flex items-center gap-1">
              <ArrowUp className="w-3 h-3" strokeWidth={2} />
              <ArrowDown className="w-3 h-3" strokeWidth={2} />
              naviguer
            </span>
            <span className="inline-flex items-center gap-1">
              <CornerDownLeft className="w-3 h-3" strokeWidth={2} />
              sélectionner
            </span>
            <span className="inline-flex items-center gap-1 ml-auto font-mono text-[10px]">
              ⌘K pour ouvrir / Esc pour fermer
            </span>
          </div>
        </div>
      </div>
    </Portal>
  );
}

export default CommandPalette;
